import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import axios from "axios";
import { saveAs } from "file-saver";
import { pdfjs, Document, Page } from "react-pdf";
import backArrow from "./../../../../assets/images/chevron-right.svg";
import shareIcon from "./../../../../assets/images/share-purple.svg";
import downloadIcon from "./../../../../assets/images/download-purple.svg";
import DDxInputSelectorBg from "./../../../../assets/images/ddx-input-selector-bg.webp";
import { message, Spin } from "antd";
import { calculateAge, trackEvent } from "../../../../utils/helper";
const worker = require("pdfjs-dist/build/pdf.worker.min.js");
pdfjs.GlobalWorkerOptions.workerSrc = worker;

const PrescriptionDetails = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const { appointmentData } = state || {};
  const {
    prescriptionLink,
    pm_first_name,
    appointment_date,
    pm_gender,
    pm_dob,
    pam_pid,
    um_id,
    um_name,
    hm_id,
    pm_contact_no,
  } = appointmentData || {};

  const [pdfError, setPdfError] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [divWidth, setDivWidth] = useState(0);
  const divRef = useRef(null);

  const moengageData = {
    "Login Mobile No": pm_contact_no,
    "Clinic Id": hm_id,
    "Time Stamp": dayjs().format("YYYY-MM-DD HH:mm:ss"),
    "Owner Doctor Name": um_name,
    "Owner Doctor Unique ID": um_id,
    "Patient Name": pm_first_name,
    "Patient Id": pam_pid,
    "Patient Age": calculateAge(pm_dob)?.years,
    "Patient Gender": pm_gender,
  };

  useEffect(() => {
    setDivWidth(((divRef.current as any)?.offsetWidth as number) - 32);
  }, [divRef]);

  useEffect(() => {
    if (prescriptionLink) {
      setPdfError(false);
    }
  }, [prescriptionLink]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleShare = async () => {
    try {
      trackEvent("AA_Share_Rx", moengageData);
      // Check if Web Share API is supported
      if (navigator.share) {
        await navigator.share({
          title: "Prescription",
          text: `Check out this prescription for ${pm_first_name} on ${dayjs(
            appointment_date
          ).format("DD MMM YYYY")}`,
          url: prescriptionLink,
        });
      } else {
        // Fallback: Copy to clipboard
        if (prescriptionLink) {
          await navigator.clipboard.writeText(prescriptionLink);
          alert("Prescription link copied to clipboard!");
        } else {
          alert("No prescription link available to share");
        }
      }
    } catch (error) {
      console.error("Error sharing prescription:", error);
      // Fallback: Copy to clipboard
      if (prescriptionLink) {
        try {
          await navigator.clipboard.writeText(prescriptionLink);
          alert("Prescription link copied to clipboard!");
        } catch (clipboardError) {
          console.error("Error copying to clipboard:", clipboardError);
          alert("Unable to share prescription");
        }
      }
    }
  };

  const handleDownload = async () => {
    if (prescriptionLink) {
      trackEvent("AA_Download_Rx", moengageData);
      try {
        const dateString = dayjs().format("DD-MM-YYYY");
        const filename = `prescription-${dateString}.pdf`;

        const response = await axios({
          url: prescriptionLink,
          method: "GET",
          responseType: "blob", // Important for binary data
        });

        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        saveAs(blob, filename);

        // Show success toast message
        message.open({
          key: "download",
          content: (
            <div className="flex items-center justify-center bg-[#171725E5]">
              <div className="text-white mr-2">
                {"Prescription downloaded successfully!"}
              </div>
            </div>
          ),
          duration: 3,
        });
      } catch (error) {
        console.error("Error downloading file:", error);

        // Show error toast message
        message.open({
          key: "download-error",
          content: (
            <div className="flex items-center justify-center bg-[#ff4d4f]">
              <div className="text-white mr-2">
                {"Download failed. Opening in new tab."}
              </div>
            </div>
          ),
          duration: 4,
        });

        // Fallback: open in new tab if download fails
        window.open(prescriptionLink, "_blank");
      }
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfError(false);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error("PDF load error:", error);
    setPdfError(true);
    message.error("Failed to load prescription PDF");
  };

  const handleOpenInNewTab = () => {
    if (prescriptionLink) {
      window.open(prescriptionLink, "_blank");
    }
  };

  return (
    <div className="App symptom-collector relative min-h-screen">
      {/* Preload PDF for faster loading */}
      {prescriptionLink && (
        <link rel="preload" href={prescriptionLink} as="document" />
      )}

      {/* Background Image */}
      <div className="fixed inset-0">
        <img
          src={DDxInputSelectorBg}
          alt="bg"
          className="h-full w-full object-cover opacity-5"
        />
      </div>

      {/* Header */}
      <div className="relative z-10 bg-white shadow-sm">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={handleBack}>
            <img src={backArrow} alt="back" className="rotate-180 w-6 h-6" />
          </button>
          <span className="text-lg font-semibold text-gray-800">
            Prescription
          </span>
          <button onClick={handleShare}>
            <img src={shareIcon} alt="share" className="rotate-180" />
          </button>
          <button onClick={handleDownload} title="Download Prescription">
            <img src={downloadIcon} alt="download" />
          </button>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="relative z-10 p-4" ref={divRef}>
        {prescriptionLink ? (
          <div className="rounded-lg shadow-sm relative">
            {pdfError ? (
              <div className="bg-white rounded-lg p-8 text-center shadow-sm">
                <p className="text-gray-500 mb-4">
                  PDF couldn't be displayed in browser
                </p>
                <button
                  onClick={handleOpenInNewTab}
                  className="bg-[#4B4AD5] text-white px-6 py-3 rounded-lg hover:bg-[#3A3A9E] transition-colors"
                >
                  Open PDF in New Tab
                </button>
              </div>
            ) : (
              <div className="overflow-auto relative bg-white h-[calc(100vh-160px)] rounded-lg overflow-y-auto overflow-x-hidden">
                <Document
                  file={prescriptionLink}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={
                    <div className="flex z-20 items-center justify-center absolute top-1/2 left-1/2">
                      <Spin size="large" />
                    </div>
                  }
                  error={
                    <div
                      style={{
                        position: "absolute",
                        zIndex: 0,
                        left: "42%",
                        top: "50%",
                      }}
                    >
                      {"Failed to load PDF file."}
                    </div>
                  }
                  noData={
                    <div
                      style={{
                        position: "absolute",
                        zIndex: 0,
                        left: "50%",
                        top: "50%",
                      }}
                    >
                      {"No PDF file specified."}
                    </div>
                  }
                >
                  {Array.apply(null, Array(numPages))
                    .map((x, i) => i + 1)
                    .map((page) => {
                      return (
                        <Page
                          key={Math.random()}
                          loading={null}
                          width={divWidth}
                          pageNumber={page}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      );
                    })}
                </Document>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg p-8 text-center shadow-sm">
            <p className="text-gray-500">No prescription link provided</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrescriptionDetails;
